'use strict';

export const summaries: {
    [rightName: string]: {
        [rightLevel: string]: string
    }
} = {
    billing: {
        none: 'Has no access to billing.',
        view: 'Can see but not edit billing information such as payment information and plan level.',
        edit: 'Can edit billing information such as payment information and plan level.'
    },
    settings: {
        none: 'Has no access to settings.',
        view: 'Can see but not edit company settings such as logo and contact information.',
        edit: 'Can change company settings such as logo, currency, and contact information.'
    },
    ownership: {
        none: 'Has no access to transactions.',
        personal: 'Can see only their own transactions.',
        view: 'Can see all transactions and the cap table.',
        edit: 'Can add and alter transactions that affect the cap table.'
    },
    corporate_authority_verification: {
        none: 'Cannot verify transactions on behalf of the corporation.',
        edit: 'Can verify transactions on behalf of the corporation.'
    },
    valuation: {
        none: 'Has no access to company valuations',
        view: 'Can see all valuations.',
        edit: 'Can edit company valuations.'
    },
    contacts: {
        personal: 'Can see and update basic information on their profile page.',
        view: 'Can see all contacts and contact info.',
        edit: 'Can create and edit contact information and permissions.'
    },
    documents: {
        personal: 'Can see documents shared with them.',
        doc_tran_view: 'Can see all documents attached to transactions.',
        view: 'Can see all documents in the library.',
        edit: 'Can upload, share, and send documents for signature.'
    },
    superuser: {
        none: 'Is limited to permissions above.',
        edit: 'Can edit their own permissions, and all permissons of others.'
    }
};

export type rightName = 'documents'|'ownership'|'authority'|'billing'|
    'settings'|'contacts'|'superuser'|'valuation'|'corporate_authority_verification';

export const rightsList = <Array<rightName>>Object.keys(summaries);

export type rightLevel = 'none'|'personal'|'edit'|'view'
    |'doc_tran_view'; // equivalent of old auditor role, will hopefully be deprecated soon
const none: rightLevel = 'none';
const personal: rightLevel = 'personal';
const edit: rightLevel = 'edit';
const view: rightLevel = 'view';

export type RightsDictionary = {
    billing?: rightLevel,
    settings?: rightLevel,
    ownership?: rightLevel,
    authority?: rightLevel,
    corporate_authority_verification?: rightLevel,
    valuation?: rightLevel,
    contacts?: rightLevel,
    documents?: rightLevel,
    superuser?: rightLevel,
    [s: string]: rightLevel
};

export const rolesMap: {[roleNames: string]: RightsDictionary} = {
    employee: {
        billing: none,
        settings: none,
        ownership: personal,
        corporate_authority_verification: none,
        valuation: none,
        contacts: personal,
        documents: personal,
        superuser: none
    },
    lawyer: {
        billing: none,
        settings: none,
        ownership: edit,
        corporate_authority_verification: edit,
        valuation: edit,
        contacts: edit,
        documents: edit,
        superuser: none
    },
    auditor: {
        billing: none,
        settings: view,
        ownership: view,
        corporate_authority_verification: none,
        valuation: view,
        contacts: view,
        documents: view,
        superuser: none
    },
    admin: {
        billing: edit,
        settings: edit,
        ownership: edit,
        corporate_authority_verification: none,
        valuation: edit,
        contacts: edit,
        documents: edit,
        superuser: none
    },
    superuser: {
        billing: edit,
        settings: edit,
        ownership: edit,
        corporate_authority_verification: edit,
        valuation: edit,
        contacts: edit,
        documents: edit,
        superuser: edit
    }
};

export const rolesList = Object.keys(rolesMap);

export const rightDisplayNames: {
    [rightName: string]: string
} = {
    billing: 'Billing',
    settings: 'Settings',
    ownership: 'Transactions',
    corporate_authority_verification: 'Verification',
    valuation: 'Valuation',
    contacts: 'Contacts',
    documents: 'Documents',
    superuser: 'Superuser'
};

export function hasEdit(rights: RightsDictionary, key: rightName) {
    return rights[key] === 'edit';
}

export function hasView(rights: RightsDictionary, key: rightName) {
    return rights[key] === 'view' || hasEdit(rights, key) ||
        (key === 'documents' && rights[key] === <rightLevel>'doc_tran_view');
}

export function hasPersonal(rights: RightsDictionary, key: rightName) {
    return hasView(rights, key) || rights[key] === 'personal' ||
        key === 'documents' || key === 'contacts'; // personal for these rights are guaranteed
}



// WEBPACK FOOTER //
// ./_src/services/contacts/rights.ts